/* eslint-disable no-unused-vars -- React required for JSX, legacy imports pending cleanup */

import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { useSelector, useDispatch } from 'react-redux';
import createDocUpload from '../../utils/createDocUpload';
import { deleteDocument, submitDocument, uploadDocument } from '../../utils/docUploadApi';
import {
  calculateSumOfSize,
  createFormDataWithFields,
  createUploadFileData,
  getAcceptedFileSum,
  getEmptyStatusMessage,
  getRejectedFileStatus,
  isBatchWithinSize,
  markDocAsDeleting,
  validateSubmitState,
} from '../../utils/docUploadCommon';
import UploadZone from '../upload/UploadZone';
import ResponseMessages from '../messages/ResponseMessages';
import FileRequirements from '../messages/FileRequirements';
import SizeBar from '../upload/SizeBar';
import DocList from '../upload/DocList';
import DeleteConfirmation from '../upload/DeleteConfirmation';
import Submit from '../upload/Submit';
import '../DocUploadStyles.scss';

function DocUploadNeverKyc(props) {
  const {
    maxFileSize,
    maxBatchSize,
    maxSizeLabel,
    maxBatchSizeLabel,
    width,
    fileTypes,
    fileTypesLabel,
    isSubmitDisabled,
    isUploadDisabled,
    submitPopLabel,
    uploadZoneView,
    fileReqView,
    docListView,
    sizeBarView,
    submitView,
    onStartUploadCall,
    onUploadCall,
    onDeleteCall,
    onSubmitCall,
    filePreview,
    downloadProgress,
    errorLabel,
    altSubmit,
    formDataProps,
    docUploadUrl,
  } = props;

  const [acceptedDocs, setAcceptedDocs] = useState([]);
  const [rejectedDocs, setRejectedDocs] = useState([]);
  const [statusMessage, setStatusMessage] = useState({
    message: '',
    type: '',
  });
  const [documentId, setDocumentId] = useState('');
  const [uploadRes, setUploadRes] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sumOfDocs, setSumOfDocs] = useState(0);
  const [isDropZoneDisabled, setIsDropZoneDisabled] = useState(false);
  const [showPopup, setShowPopup] = useState({});
  const [forceValue, setForceValue] = useState(0);

  const dispatch = useDispatch();
  const locale = useSelector((state) => state.getIn(['intl', 'activeLocale']));

  useEffect(() => {
    dispatch(loadLanguagePack('axp-document-uploader', { fallbackLocale: 'en-US' }));
  }, [dispatch, locale]);

  const handleApiResponse = (onApiResponse, res) => {
    setForceValue((value) => value + 1);
    if (onApiResponse) onApiResponse(res);
  };

  useEffect(() => {
    setForceValue((value) => value + 1);
  }, [acceptedDocs]);

  // Initial document setup should run once on mount.
  useEffect(() => {
    const formData = createFormDataWithFields(formDataProps);
    formData.append('documentId', 'INITIALDOC');

    if (isUploadDisabled) {
      setIsDropZoneDisabled(true);
    } else {
      createDocUpload(dispatch, formData, docUploadUrl).then(
        (res) => {
          if (res.status && res.status.statusCode === '50200') {
            setDocumentId(res.documentId);
            handleApiResponse(onUploadCall, res);
          } else {
            setIsDropZoneDisabled(true);
            setStatusMessage({
              ...statusMessage,
              message: 'uploadApiError',
              type: 'error',
            });
          }
        }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only initialization by design

  const onResetMessages = () => {
    setStatusMessage(getEmptyStatusMessage());
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    let filesMap;
    let fileSum = getAcceptedFileSum(acceptedFiles, sumOfDocs);

    onResetMessages();

    if (rejectedFiles.length > 0) {
      setStatusMessage(getRejectedFileStatus(rejectedFiles[0], maxFileSize, maxSizeLabel));
    }

    if (isBatchWithinSize(acceptedFiles, fileSum, maxBatchSize)) {
      setStatusMessage(getEmptyStatusMessage());

      filesMap = acceptedFiles.map((file) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          const fileData = createUploadFileData(file, e.currentTarget.result);
          resolve(fileData);

          const { response, outcome } = await uploadDocument({
            docUploadUrl,
            formDataProps,
            file,
            documentId,
            onStartUploadCall,
            onProgress: (event) => {
              fileData.progress = (Number((event.loaded / event.total) * 100).toFixed(0));
              setForceValue((value) => value + 1);
            },
          });

          if (outcome === 'success') {
            fileData.progress = null;
            fileData.fileId = response.fileList[response.fileList.length - 1].fileId;
            setUploadRes(response);
            setDocumentId(response.documentId);
            handleApiResponse(onUploadCall, response);
            setForceValue((value) => value + 1);
          } else {
            fileSum -= file.size;
            setSumOfDocs(fileSum);
            setStatusMessage({ message: outcome, type: 'error' });
            setAcceptedDocs(
              ...acceptedDocs,
              acceptedDocs.filter((docs) => docs.fileId !== fileData.fileId)
            );
          }
        };
        reader.readAsDataURL(file);
      }));
    } else {
      if (acceptedFiles.length > 0) {
        fileSum -= acceptedFiles[0].size;
        setStatusMessage({
          message: 'totalSizeExceedsError',
          type: 'error',
          totalSize: maxBatchSizeLabel,
        });
      }
      filesMap = [];
    }

    return Promise.all(filesMap).then((result) => {
      setAcceptedDocs([...acceptedDocs, ...result]);
      setRejectedDocs(rejectedFiles);
      setSumOfDocs(fileSum);
    });
  };

  const deleteDoc = (idx, fileId) => {
    onResetMessages();

    setAcceptedDocs((prevDocs) => markDocAsDeleting(prevDocs, fileId));
    setIsDeleting(true);

    return deleteDocument({
      dispatch,
      documentId: uploadRes.documentId,
      fileId,
      formDataProps,
    }).then(({ response: res, outcome: deleteOutcome }) => {
      setIsDeleting(false);
      if (deleteOutcome === 'success') {
        setSumOfDocs((prevSum) => calculateSumOfSize(acceptedDocs, fileId));
        setAcceptedDocs((prevDocs) => prevDocs.filter((docs) => docs.fileId !== fileId));
        setShowPopup({
          idx: null,
          fileId: null,
          fileName: null,
          popupType: '',
        });
        handleApiResponse(onDeleteCall, res);
      } else {
        setStatusMessage({ message: deleteOutcome, type: 'error' });
      }
    }).catch(() => {
      setIsDeleting(false);
      setStatusMessage({
        message: 'deleteApiError',
        type: 'error',
      });
    });
  };

  const submitDoc = () => {
    onResetMessages();

    const submitValidationMessage = validateSubmitState({
      acceptedDocs,
      isSubmitDisabled,
      isDeleting,
    });
    if (submitValidationMessage) {
      setStatusMessage({
        message: submitValidationMessage,
        type: 'error',
      });
      return null;
    }

    setIsSubmitting(true);

    return submitDocument({
      dispatch,
      documentId: uploadRes.documentId,
      formDataProps,
    }).then(({ response: res, outcome: submitOutcome }) => {
      if (submitOutcome === 'success') {
        setStatusMessage({
          message: 'submitSuccessLabel',
          type: 'success',
        });
        setIsSubmitting(false);
        setAcceptedDocs([]);
        setIsDropZoneDisabled(true);

        handleApiResponse(onSubmitCall, res);
      } else {
        setStatusMessage({
          message: submitOutcome,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    });
  };

  // eslint-disable-next-line max-params -- function signature defined by external contract
  const handlePopupShow = (idx, fileId, fileName, show, popupType, confirm) => {
    if (altSubmit !== null) {
      altSubmit();
      return null;
    }
    if (confirm) {
      setShowPopup({
        show,
        popupType,
      });
      return popupType === 'delete' ? deleteDoc(idx, fileId) : submitDoc();
    }
    setShowPopup({
      show,
      idx,
      fileId,
      fileName,
      popupType,
    });
    return null;
  };

  const hasUploadedDocs = acceptedDocs.some((doc) => doc.progress === null);

  return (
    <div className="doc-upload-width-shell duak-outer-container dnuk-flow-never-kyc">
      <div className="dnuk-label-row flex flex-start flex-align-start">
        <div className="dnuk-label-text dls-grey-03 flex-item-grow width-full">
          <FormattedMessage id="selectFile" />
        </div>
      </div>
      <div className="margin-1-t pad-1-b">
        <FileRequirements
          id="docUploadFileRequirements"
          maxSizeLabel={maxSizeLabel}
          maxBatchSizeLabel={maxBatchSizeLabel}
          fileTypesLabel={fileTypesLabel}
          fileReqView={fileReqView}
        />
      </div>
      <div
        className="flex flex-column flex-align-start flex-align-self-stretch flex-item-grow margin-4-t dnuk-content-stack"
      >
        <UploadZone
          onDrop={onDrop}
          sumOfDocs={sumOfDocs}
          isDropZoneDisabled={isDropZoneDisabled}
          maxFileSize={maxFileSize}
          maxBatchSize={maxBatchSize}
          fileTypes={fileTypes}
          uploadZoneView={uploadZoneView}
        />
        <DocList
          acceptedDocs={acceptedDocs}
          handlePopupShow={handlePopupShow}
          docListView={docListView}
          documentId={documentId}
          filePreview={filePreview}
          downloadProgress={downloadProgress}
        />
        <ResponseMessages
          statusMessage={statusMessage}
          errorLabel={errorLabel}
        />
        <Submit
          sumOfDocs={sumOfDocs}
          submitDoc={submitDoc}
          handlePopupShow={handlePopupShow}
          isSubmitting={isSubmitting}
          isSubmitDisabled={isSubmitDisabled || !hasUploadedDocs}
          submitView={submitView}
          maxBatchSizeLabel={maxBatchSizeLabel}
          submitPopLabel={submitPopLabel}
        />
        <DeleteConfirmation
          showPopup={showPopup}
          handlePopupShow={handlePopupShow}
          submitPopLabel={submitPopLabel || ''}
        />
      </div>
    </div>
  );
}

DocUploadNeverKyc.propTypes = {
  fileTypes: PropTypes.string.isRequired,
  fileTypesLabel: PropTypes.string,
  maxFileSize: PropTypes.number,
  maxBatchSize: PropTypes.number,
  maxSizeLabel: PropTypes.string,
  maxBatchSizeLabel: PropTypes.string,
  width: PropTypes.string,
  isSubmitDisabled: PropTypes.bool,
  isUploadDisabled: PropTypes.bool,
  uploadZoneView: PropTypes.string,
  fileReqView: PropTypes.string,
  docListView: PropTypes.string,
  sizeBarView: PropTypes.string,
  submitView: PropTypes.string,
  formDataProps: PropTypes.instanceOf(Object).isRequired,
  docUploadUrl: PropTypes.string,
  submitPopLabel: PropTypes.string,
  onStartUploadCall: PropTypes.func,
  onUploadCall: PropTypes.func,
  onDeleteCall: PropTypes.func,
  onSubmitCall: PropTypes.func,
  filePreview: PropTypes.func,
  downloadProgress: PropTypes.instanceOf(Object),
  errorLabel: PropTypes.string,
  altSubmit: PropTypes.func,
};

DocUploadNeverKyc.defaultProps = {
  maxFileSize: 30000000,
  maxBatchSize: 100000000,
  maxSizeLabel: '30 MB',
  maxBatchSizeLabel: '100 MB',
  width: '69rem',
  fileTypesLabel: '',
  isSubmitDisabled: false,
  isUploadDisabled: false,
  submitPopLabel: null,
  uploadZoneView: 'ViewOne',
  fileReqView: 'ViewOne',
  docListView: 'ViewOne',
  sizeBarView: 'ViewOne',
  submitView: 'ViewOne',
  onStartUploadCall: null,
  onUploadCall: null,
  onDeleteCall: null,
  onSubmitCall: null,
  filePreview: null,
  downloadProgress: null,
  errorLabel: null,
  altSubmit: null,
};

export default DocUploadNeverKyc;
/* eslint-enable no-unused-vars -- React required for JSX, legacy imports pending cleanup */
